import React from "react";
import { MonthBucket } from "../../types/PublicListingStats";

interface TrendlineChartProps {
  title: string;
  data: MonthBucket[];
  /** Raw amounts corresponding to each data point (enables Y-axis labels) */
  amounts?: number[];
  /** Format a raw amount for Y-axis labels (e.g. cents -> "$4K") */
  formatAmount?: (value: number) => string;
  color?: string;
}

const CHART_WIDTH = 540;
const CHART_HEIGHT = 140;
const PADDING_RIGHT = 4;
const PADDING_TOP = 8;
const PADDING_BOTTOM = 24;

function formatMonthLabel(monthKey: string): string {
  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString("en-US", { month: "short" });
}

function formatMonthYear(monthKey: string): string {
  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/** Pick ~4 nice round tick values from 0 to max. */
function computeTicks(max: number): number[] {
  if (max <= 0) return [0];

  const rough = max / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const nice = [1, 2, 2.5, 5, 10];
  let step = magnitude;
  for (const n of nice) {
    if (n * magnitude >= rough) {
      step = n * magnitude;
      break;
    }
  }

  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) {
    ticks.push(Math.round(v * 100) / 100);
  }
  // Ensure we have at least the max tick
  if (ticks[ticks.length - 1] < max) {
    ticks.push(ticks[ticks.length - 1] + step);
  }
  return ticks;
}

const TrendlineChart: React.FC<TrendlineChartProps> = ({
  title,
  data,
  amounts,
  formatAmount,
  color = "#623cea",
}) => {
  if (data.length === 0) return null;

  // Trim leading zero-value months so the chart focuses on meaningful data
  const firstNonZero = amounts
    ? amounts.findIndex((v) => v > 0)
    : data.findIndex((d) => d.normalized > 0);
  const trimIndex = firstNonZero > 0 ? firstNonZero : 0;
  const trimmedData = data.slice(trimIndex);
  const trimmedAmounts = amounts?.slice(trimIndex);

  if (trimmedData.length === 0) return null;

  const hasYAxis =
    trimmedAmounts && formatAmount && trimmedAmounts.length === trimmedData.length;

  // Compute Y-axis ticks when amounts are provided
  const maxAmount = hasYAxis ? Math.max(...trimmedAmounts) : 0;
  const ticks = hasYAxis ? computeTicks(maxAmount) : [];

  // Dynamically size y-axis width based on longest label (~7px per char)
  const paddingLeft = hasYAxis
    ? Math.max(...ticks.map((t) => formatAmount(t).length)) * 7 + 8
    : 0;
  const plotWidth = CHART_WIDTH - paddingLeft - PADDING_RIGHT;
  const plotHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

  const gradientId = `gradient-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const yMax = ticks.length > 0 ? ticks[ticks.length - 1] : 100;

  // Build points for the line
  const points = trimmedData.map((d, i) => {
    const x = paddingLeft + (i / (trimmedData.length - 1)) * plotWidth;
    let yFrac: number;
    if (hasYAxis && yMax > 0) {
      yFrac = trimmedAmounts[i] / yMax;
    } else {
      yFrac = d.normalized / 100;
    }
    const y = PADDING_TOP + plotHeight - yFrac * plotHeight;
    return { x, y, month: d.month };
  });

  // Build SVG path for the line
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  // Build SVG path for the filled area
  const areaPath =
    linePath +
    ` L ${points[points.length - 1].x} ${PADDING_TOP + plotHeight}` +
    ` L ${points[0].x} ${PADDING_TOP + plotHeight} Z`;

  // Month labels: show every other month if > 6 points
  const labelInterval = trimmedData.length > 6 ? 2 : 1;

  return (
    <div className="trendline-chart">
      <div className="trendline-header">
        <div>
          <span className="trendline-title">{title}</span>
          {trimmedData.length >= 2 && (
            <div className="trendline-date-range">
              {formatMonthYear(trimmedData[0].month)} &ndash;{" "}
              {formatMonthYear(trimmedData[trimmedData.length - 1].month)}
            </div>
          )}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        className="trendline-svg"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* Y-axis ticks and grid lines */}
        {hasYAxis &&
          ticks.map((tick) => {
            const yFrac = yMax > 0 ? tick / yMax : 0;
            const y = PADDING_TOP + plotHeight - yFrac * plotHeight;
            return (
              <g key={tick}>
                <line
                  x1={paddingLeft - 4}
                  x2={CHART_WIDTH - PADDING_RIGHT}
                  y1={y}
                  y2={y}
                  className="trendline-grid-line"
                />
                <text
                  x={2}
                  y={y + 3.5}
                  textAnchor="start"
                  className="trendline-y-label"
                >
                  {formatAmount(tick)}
                </text>
              </g>
            );
          })}

        {/* Filled area */}
        <path d={areaPath} fill={`url(#${gradientId})`} />

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Month labels */}
        {points.map((p, i) =>
          i % labelInterval === 0 ? (
            <text
              key={p.month}
              x={p.x}
              y={CHART_HEIGHT - 4}
              textAnchor="middle"
              className="trendline-month-label"
            >
              {formatMonthLabel(p.month)}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
};

export default TrendlineChart;
